import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_TIMEOUT_MS = 15000;

function normalizedError(error) {
  if (error?.code === 'TIMEOUT') {
    return {
      message: 'پاسخی از سرور دریافت نشد. دوباره تلاش کنید.',
      code: 'TIMEOUT',
    };
  }

  if (error instanceof TypeError || /network|failed to fetch|load failed/i.test(error?.message || '')) {
    return {
      message: 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.',
      code: 'NETWORK',
    };
  }

  return {
    message: error?.userMessage || 'دریافت اطلاعات از سرور ناموفق بود. دوباره تلاش کنید.',
    code: 'SERVER',
  };
}

export function useAsync(loader, deps, options = {}) {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let timeoutId;

    setState({ status: 'loading', data: null, error: null });

    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        const error = new Error('Request timed out');
        error.code = 'TIMEOUT';
        reject(error);
      }, timeoutMs);
    });

    const request = Promise.resolve()
      .then(() => loaderRef.current(controller.signal))
      .then((value) => {
        if (value?.error) throw value.error;
        return value;
      });

    Promise.race([request, timeout])
      .then((data) => {
        if (active) setState({ status: 'ready', data, error: null });
      })
      .catch((error) => {
        if (active) setState({ status: 'error', data: null, error: normalizedError(error) });
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      active = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
    // The caller controls reruns through deps, matching useEffect's contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, timeoutMs, ...deps]);

  return { ...state, retry };
}
